import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Loader2 } from 'lucide-react';

/**
 * Unified create/edit form dialog.
 * props:
 *  - open, onOpenChange
 *  - title, description
 *  - fields: [{ name, label, type?, options?, placeholder?, required?, defaultValue?, full? }]
 *  - initialValues?: object (if provided, switches to edit mode - button label becomes "Enregistrer")
 *  - onSubmit: async (values) => created/updated item
 *  - submitLabel?
 */
export default function FormDialog({ open, onOpenChange, title, description, fields, onSubmit, initialValues, submitLabel }) {
  const computeInitial = () => fields.reduce((acc, f) => {
    if (initialValues && initialValues[f.name] !== undefined) {
      acc[f.name] = initialValues[f.name];
    } else if (f.defaultValue !== undefined) {
      acc[f.name] = f.defaultValue;
    } else {
      acc[f.name] = f.type === 'number' ? 0 : '';
    }
    return acc;
  }, {});

  const [values, setValues] = useState(computeInitial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setValues(computeInitial());
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues]);

  const setVal = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  const finalLabel = submitLabel || (initialValues ? 'Enregistrer' : 'Créer');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[18px]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</DialogTitle>
          {description && <DialogDescription className="text-[13px]">{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3.5 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 max-h-[60vh] overflow-y-auto pr-1">
            {fields.filter((f) => typeof f.showIf !== 'function' || f.showIf(values)).map((f) => (
              <div key={f.name} className={f.full ? 'sm:col-span-2' : ''}>
                <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">
                  {f.label}{f.required && <span className="text-[#EF4444] ml-0.5">*</span>}
                </label>
                {f.type === 'select' ? (
                  <select
                    value={values[f.name] || ''}
                    onChange={(e) => setVal(f.name, e.target.value)}
                    required={f.required}
                    className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
                  >
                    <option value="">{f.placeholder || 'Sélectionner…'}</option>
                    {(f.options || []).map((o) => {
                      const v = typeof o === 'string' ? o : o.value;
                      const l = typeof o === 'string' ? o : (o.label ?? o.value);
                      return <option key={v} value={v}>{l}</option>;
                    })}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea
                    value={values[f.name] || ''}
                    onChange={(e) => setVal(f.name, e.target.value)}
                    placeholder={f.placeholder}
                    required={f.required}
                    rows={3}
                    className="w-full px-3 py-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors resize-none"
                  />
                ) : f.type === 'color-palette' ? (
                  <div className="flex flex-wrap gap-2">
                    {(f.options || []).map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        data-testid={`color-swatch-${c.value.replace('#','')}`}
                        onClick={() => setVal(f.name, c.value)}
                        className={`w-9 h-9 rounded-full border-2 transition-all flex items-center justify-center ${values[f.name] === c.value ? 'border-[#1A1B2E] scale-110 shadow-md' : 'border-white shadow-sm hover:scale-105'}`}
                        style={{ background: c.value }}
                        title={c.label}
                      >
                        {values[f.name] === c.value && <span className="w-2 h-2 rounded-full bg-white" />}
                      </button>
                    ))}
                  </div>
                ) : (
                  <input
                    type={f.type || 'text'}
                    value={values[f.name] ?? ''}
                    onChange={(e) => setVal(f.name, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                    placeholder={f.placeholder}
                    required={f.required}
                    className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
                  />
                )}
              </div>
            ))}
          </div>
          {error && (
            <div className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>
          )}
          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE] transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95 transition-opacity disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {finalLabel}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
